import { Paragraph } from "types/Paragraph";
import { RevisionAnnotations } from "types/RevisionAnnotation";

export const usePDFParagraphGenerator = (
  paragraph: Paragraph,
  annotationMap: { [key: number]: RevisionAnnotations },
  annotationParagraphMap: { [key: string]: RevisionAnnotations },
  endLineAnnotations: { [key: string]: RevisionAnnotations },
  previousParagraphCollectionRef: string | undefined,
  commentColor: number[],
  isOSS: boolean
) => {
  const deferredAnnotations: RevisionAnnotations = [];

  let assignmentParagraphs = [] as any[];

  if (
    isOSS &&
    paragraph.collectionRef == "paragraphs" &&
    paragraph.localIndex == 0
  ) {
    assignmentParagraphs.push({
      text: "Writing Assignment",
      fontSize: 32,
      bold: true,
    });
  }

  assignmentParagraphs.push({ text: "", pageBreak: "before" });

  const paragraphTokenGroups = useConvertTokensToGroups(paragraph.tokens);

  let alignment: string = "left";
  const spacing = paragraph.formatting.lineSpacing;

  switch (paragraph.formatting.alignment) {
    case "CENTER":
      alignment = "center";
      break;
    case "RIGHT":
      alignment = "right";
      break;
    case "JUSTIFIED":
      alignment = "justified";
      break;
    case "LEFT":
    default:
      alignment = "left";
  }

  let didAddParagraphAnnotation = false;

  for (const tokenGroup of paragraphTokenGroups) {
    const textRuns = [] as any[];

    if (
      paragraph.formatting.firstLineIndent ||
      paragraph.tokens[0]?.type == "space" ||
      paragraph.tokens[0]?.type == "tab"
    ) {
      textRuns.push({
        text: "\u200B\t\u200B\t",
      });
    }

    for (const token of tokenGroup) {
      if (token.type == "page_break") {
        textRuns.push({ text: "", pageBreak: "before" });
        continue;
      }

      const fontSize: number = token.formatting?.size ?? 12;
      const fontFamily = token.formatting?.font ?? "Tinos";
      const textRun = useConvertTokenToPDFText(token, alignment, spacing);

      let preAnnotationRuns: any[] = [];
      let postAnnotationRuns: any[] = [];

      const idAnnotations = annotationMap[token.globalPosition];

      if (idAnnotations) {
        for (var an of idAnnotations) {
          if (an) {
            // @ts-ignore
            let annotationTextRuns = useConvertQuillDeltaToPDFRun(
              an.commentObject,
              fontSize,
              useRGBToHex(commentColor),
              false
            ).flat();

            if (useIsAnnotationCommentLong(an)) {
              deferredAnnotations.push(an);
              annotationTextRuns = [
                {
                  text: "See below.",
                  color: useRGBToHex(commentColor),
                  fontSize: fontSize,
                },
              ];
            }

            if (token.type == "filler" || token.type == "space") {
              annotationTextRuns.push({
                text: " ",
                color: useRGBToHex(commentColor),
                fontSize: fontSize,
              });
            } else {
              // add a ext run to the beginning of the annotation text runs
              annotationTextRuns = [
                {
                  text: " ",
                  color: useRGBToHex(commentColor),
                  fontSize: fontSize,
                },
                ...annotationTextRuns,
              ];
            }

            // If the annotation is only attached to one token then we put the annotation before the token
            // Other wise we'll render the annotations after the last token

            // IEW requested annotations always come after so I'm doing htat here but will leae in the old code.
            if (
              (an.tokenGlobalPositions.length > 1 ||
                an.forceAfterToken ||
                true) &&
              an.isStartLine != true
            ) {
              postAnnotationRuns.push(...annotationTextRuns);
            }

            if (an.isStartLine == true) {
              preAnnotationRuns.push(...annotationTextRuns);
            }

            // } else {
            //   preAnnotationRuns.push(annotationRun);
            // }
          }
        }
      }

      const indexOfToken = tokenGroup.indexOf(token);
      const isLastTokenInGroup = indexOfToken == tokenGroup.length - 1;

      const paragraphEndLineAnnotations = endLineAnnotations[paragraph.id];

      if (isLastTokenInGroup && paragraphEndLineAnnotations) {
        for (var an of paragraphEndLineAnnotations) {
          if (an) {
            // If the annotation is attached to the last token of a particular group and
            // that value doesn't match this last token, skip over attachign this annotation.
            if (
              an.lastTokenGlobalPosition &&
              an.lastTokenGlobalPosition != token.globalPosition
            ) {
              continue;
            }

            // @ts-ignore
            const annotationTextRuns = useConvertQuillDeltaToPDFRun(
              an.commentObject,
              fontSize,
              useRGBToHex(commentColor),
              false
            ).flat();

            postAnnotationRuns.push(...annotationTextRuns, {
              text: "  ",
            });
          }
        }
      }

      if (preAnnotationRuns.length > 0) {
        textRuns.push(...preAnnotationRuns);
      }

      textRuns.push(textRun);

      if (postAnnotationRuns.length > 0) {
        textRuns.push(
          {
            text: " ",
          },
          ...postAnnotationRuns
        );
      }

      preAnnotationRuns = [];
      postAnnotationRuns = [];
    }

    const paragraphObject = {
      text: textRuns,
      alignment: alignment,
      lineHeight: spacing,
    } as any;

    if (paragraph.formatting.leftIndent) {
      paragraphObject.text = [
        {
          text: "\u200B\t",
        },
        ...textRuns,
      ];
    }

    assignmentParagraphs.push(paragraphObject);

    // Now we insert deferred annotations or extra annotaitons

    // Here we insert any deffered annotations or paragraph annotaitons between token groups as necessary
    const paragraphId = paragraph.id;

    for (const deferredAnnotation of deferredAnnotations) {
      deferredAnnotation.tokenGlobalPositions.sort();

      if (deferredAnnotation.tokenGlobalPositions.length == 0) continue;

      const lastTokenPosition =
        deferredAnnotation.tokenGlobalPositions[
          deferredAnnotation.tokenGlobalPositions.length - 1
        ];

      const indexInGroup = tokenGroup.findIndex(
        (t) => t.globalPosition == lastTokenPosition
      );

      // If the deferred annotqtion is not linked to this particualr group then we need to skip
      if (indexInGroup < 0) continue;

      const deferredAnnotationRuns: any[] = [
        {
          text: "\n\n",
        },
      ];

      const tokenText = deferredAnnotation.tokenText;

      if (tokenText != "") {
        const tokenTextAnnotationRun = {
          fontSize: 12,
          text: `${tokenText} - `,
          color: useRGBToHex(commentColor),
          bold: true,
        };

        deferredAnnotationRuns.push(tokenTextAnnotationRun);
      }

      const annotationTextRuns = useConvertQuillDeltaToPDFRun(
        deferredAnnotation.commentObject,
        12,
        useRGBToHex(commentColor),
        true
      )
        .flat()
        .filter((r) => r.text != "");

      annotationTextRuns.push({
        text: "\n",
      });

      deferredAnnotationRuns.push(...annotationTextRuns);

      assignmentParagraphs.push({
        text: deferredAnnotationRuns,
      });

      if (deferredAnnotationRuns.length > 0) {
        didAddParagraphAnnotation = true;
      }
    }

    const paragraphAnnotations = annotationParagraphMap[paragraphId] ?? [];

    if (paragraphAnnotations.length > 0) {
      assignmentParagraphs.push({
        text: "\n",
      });
    }

    for (const an of paragraphAnnotations) {
      // if we have a lasttokenglobalPosition then we need to check if it's in this group
      // If the lastTokenglobalposition for this annotation isn't in this group then we skip it.
      if (an.lastTokenGlobalPosition) {
        const index = tokenGroup.findIndex(
          (t) => t.globalPosition == an.lastTokenGlobalPosition
        );

        if (index < 0) continue;
      }

      const deferredAnnotationRuns: any[] = [];

      const annotationTextRuns = useConvertQuillDeltaToPDFRun(
        an.commentObject,
        12,
        useRGBToHex(commentColor),
        true
      )
        .flat()
        .filter((r) => r.text != "");

      annotationTextRuns.push({
        text: "\n",
      });

      deferredAnnotationRuns.push(...annotationTextRuns);

      if (an.isBeforeParagraph == true) {
        assignmentParagraphs = [
          {
            text: deferredAnnotationRuns,
          },
          ...assignmentParagraphs,
        ];
      } else {
        assignmentParagraphs.push({
          text: deferredAnnotationRuns,
        });
      }

      if (deferredAnnotationRuns.length > 0) {
        didAddParagraphAnnotation = true;
      }
    }
  }

  if (didAddParagraphAnnotation) {
    assignmentParagraphs.push({
      text: "\n",
    });
  }

  return {
    text: assignmentParagraphs,
  };
};
